import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { ICaseStatusTypesService } from "services/CaseStatusTypesService";
import { SETTING_TYPES } from "services/userSettings/SettingTypes";
import { IUserSettingsService } from "services/UserSettingsService";
import { CaseStatusTypeModel, NewCaseStatusTypeModel } from "services/api/cplapi/CPLAPI";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";

const initialState = {
  CaseStatusTypes: new Array<CaseStatusTypeModel>(),
  LoadingCaseStatusTypes: false,
  EditingCaseStatusTypes: false,
  SavedColumnOrder: new Array<string>(),
};

export const caseStatusTypesSlice = createSlice({
  name: "caseStatusTypes",
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(getCaseStatusTypes.pending, (state) => {
        state.LoadingCaseStatusTypes = true;
      })
      .addCase(getCaseStatusTypes.fulfilled, (state, action) => {
        state.CaseStatusTypes = action.payload;
        state.LoadingCaseStatusTypes = false;
      })
      .addCase(getCaseStatusTypes.rejected, (state) => {
        state.LoadingCaseStatusTypes = false;
      })
      .addCase(createCaseStatusType.pending, (state) => {
        state.EditingCaseStatusTypes = true;
      })
      .addCase(updateCaseStatusType.pending, (state) => {
        state.EditingCaseStatusTypes = true;
      })
      .addCase(deleteCaseStatusType.pending, (state) => {
        state.EditingCaseStatusTypes = true;
      })
      .addCase(createCaseStatusType.rejected, (state) => {
        state.EditingCaseStatusTypes = false;
      })
      .addCase(updateCaseStatusType.rejected, (state) => {
        state.EditingCaseStatusTypes = false;
      })
      .addCase(deleteCaseStatusType.rejected, (state) => {
        state.EditingCaseStatusTypes = false;
      })
      .addCase(createCaseStatusType.fulfilled, (state) => {
        toast.success("Case Status Type Created");
        state.EditingCaseStatusTypes = false;
      })
      .addCase(updateCaseStatusType.fulfilled, (state) => {
        toast.success("Case Status Type Updated");
        state.EditingCaseStatusTypes = false;
      })
      .addCase(deleteCaseStatusType.fulfilled, (state) => {
        toast.success("Case Status Type Deleted");
        state.EditingCaseStatusTypes = false;
      })
      .addCase(getCaseStatusTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.payload;
      })
      .addCase(updateCaseStatusTypesTableColumnOrder.fulfilled, (state, action) => {
        state.SavedColumnOrder = action.meta.arg;
        toast.success("Column order saved");
      });
  },
});

export const getCaseStatusTypes = createAsyncThunk("CaseStatusTypes/GetCaseStatusTypes", async () => {
  const caseStatusTypesService = container.get<ICaseStatusTypesService>(SERVICE_TYPES.CaseStatusTypesService);
  return await caseStatusTypesService.getCaseStatusTypes();
});

export const createCaseStatusType = createAsyncThunk<
  void,
  { newCaseStatusTypeModel: NewCaseStatusTypeModel },
  { state: RootState }
>("CaseStatusTypes/CreateCaseStatusTypes", async ({ newCaseStatusTypeModel }, thunkAPI) => {
  const caseStatusTypesService = container.get<ICaseStatusTypesService>(SERVICE_TYPES.CaseStatusTypesService);
  await caseStatusTypesService.createCaseStatusType(newCaseStatusTypeModel);
  thunkAPI.dispatch(getCaseStatusTypes());
});

export const updateCaseStatusType = createAsyncThunk<
  void,
  { caseStatusTypeModel: CaseStatusTypeModel },
  { state: RootState }
>("CaseStatusTypes/UpdateCaseStatusType", async ({ caseStatusTypeModel }, thunkAPI) => {
  const caseStatusTypesService = container.get<ICaseStatusTypesService>(SERVICE_TYPES.CaseStatusTypesService);
  await caseStatusTypesService.updateCaseStatusType(caseStatusTypeModel);
  thunkAPI.dispatch(getCaseStatusTypes());
});

export const deleteCaseStatusType = createAsyncThunk<void, { caseStatusTypeID: number }, { state: RootState }>(
  "CaseStatusTypes/DeleteCaseStatusType",
  async ({ caseStatusTypeID }, thunkAPI) => {
    const caseStatusTypesService = container.get<ICaseStatusTypesService>(SERVICE_TYPES.CaseStatusTypesService);
    await caseStatusTypesService.deleteCaseStatusType(caseStatusTypeID);
    thunkAPI.dispatch(getCaseStatusTypes());
  }
);

export const getCaseStatusTypesTableColumnOrder = createAsyncThunk(
  "CaseStatusTypes/GetCaseStatusTypesTableColumnOrder",
  async () => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    return await userSettingsService.getColumnOrder(SETTING_TYPES.CaseStatusTypesTableColumnKey);
  }
);

export const updateCaseStatusTypesTableColumnOrder = createAsyncThunk(
  "CaseStatusTypes/UpdateCaseStatusTypesTableColumnOrder",
  async (tableColumnOrder: string[]) => {
    const userSettingsService = container.get<IUserSettingsService>(SERVICE_TYPES.UserSettingsService);
    await userSettingsService.setColumnOrder(tableColumnOrder, SETTING_TYPES.CaseStatusTypesTableColumnKey);
  }
);

export const selectCaseStatusTypes = (state: RootState) => {
  return state.caseStatusTypes.CaseStatusTypes;
};
export const selectLoadingCaseStatusTypes = (state: RootState) => {
  return state.caseStatusTypes.LoadingCaseStatusTypes;
};
export const selectEditingCaseStatusTypes = (state: RootState) => {
  return state.caseStatusTypes.EditingCaseStatusTypes;
};

export const selectSavedColumnOrder = (state: RootState) => {
  return state.caseStatusTypes.SavedColumnOrder;
};

export default caseStatusTypesSlice.reducer;
